import {
  LAND_SECTION_DESCRIPTIONS,
  withDescriptions,
} from './sectionDescriptions'
import {
  calc,
  geo,
  POPETY_SCORE_DEFINITIONS,
  round0,
  round1,
  type SectionField,
  type SectionSpec,
} from './sectionFields'
import {
  historySpec,
  marketSpec,
  savedListingPriceHistory,
  savedListingRows,
  savedPermitRows,
  sharedAnalysisSpecs,
  transactionRows,
} from './analysisShared'

/**
 * Land-analysis sections, declared against production `land_analysis_public_share`
 * rows (170 with an array `section`), never against the TypeScript types —
 * 20 of the 22 fields on `LandAnalysisShareValue` are `any`.
 *
 * `purchase_listings`, `rental_listings`, `transactions` and
 * `construction_permits` hold what the customer explicitly SAVED on the
 * analysis, as full documents. 120 of 155 production shares store them as
 * arrays; only 10 rows from February 2026 carry the legacy
 * `{ land_id, message: "… loaded separately in the UI" }` stub, which resolves
 * to nothing here and is simply omitted.
 *
 * `feasibility` IS a genuine stub — "Feasibility studies are not included in
 * public shares" — an explicit product decision, surfaced in `about.excluded`.
 */

/**
 * Zoning indices, named as the public API names them. The suffixes are the
 * official bilingual abbreviations: `ius_az` is indice d'utilisation du sol /
 * Ausnützungsziffer, `ibus_gfz` indice brut d'utilisation / Geschossflächen-
 * ziffer, `ios_uz` / `cos` the coverage ratios, `im_bmz` the volume ratio.
 *
 * FAR ratios (ius/ibus/cus) multiply the parcel area to a GROSS FLOOR AREA;
 * coverage ratios (ios/cos) multiply it to a FOOTPRINT. They are not
 * interchangeable, which is why each keeps its own name here rather than
 * being flattened into one "density" figure.
 */
const reg = (
  path: string,
  key: string,
  unit?: 'ratio' | 'm' | 'm2' | 'm3' | 'count' | 'pct'
): SectionField => ({ path, key, unit, source: 'regulation' })

/**
 * These are dimensionless RATIOS, not percentages: the public API documents
 * `ius_az` as "net floor area divided by parcel area", example 0.92. Emitting
 * 0.13 under a `pct` label would tell a reader the parcel may be built to
 * 0.13% of its area — wrong by a factor of 100. They are `regulation`-sourced
 * because they come from the land-use plan, not from our own analysis.
 */
const indices = (prefix: string): SectionField[] => [
  reg(`${prefix}ius`, 'ius_az', 'ratio'),
  reg(`${prefix}ibus`, 'ibus_gfz', 'ratio'),
  reg(`${prefix}cus`, 'cus', 'ratio'),
  reg(`${prefix}ios`, 'ios_uz', 'ratio'),
  reg(`${prefix}cos`, 'cos', 'ratio'),
]

const planRows: SectionField = {
  path: 'landUsePlans',
  key: 'land_use_plans',
  source: 'regulation',
  kind: 'series',
  item: [
    { path: 'name', key: 'zone_name', source: 'regulation' },
    { path: 'plan_name', key: 'plan_name', source: 'regulation' },
    { path: 'plan_type', key: 'plan_type', source: 'regulation' },
    { path: 'status', key: 'status', source: 'regulation' },
    { path: 'adoption_date', key: 'adoption_date', source: 'regulation' },
    ...indices(''),
    // Volume ratio (indice de masse / Baumassenziffer): m3 of building per
    // m2 of parcel. Dimensionless, like the floor-area ratios above.
    reg('im', 'im_bmz', 'ratio'),
    {
      path: 'height',
      key: 'max_height',
      unit: 'm',
      source: 'regulation',
      transform: round1,
    },
    {
      path: 'floor_nb',
      key: 'max_floor_nb',
      unit: 'count',
      source: 'regulation',
    },
    {
      path: 'distance_limit',
      key: 'distance_limit',
      unit: 'm',
      source: 'regulation',
      transform: round1,
    },
    {
      path: 'unit_nb',
      key: 'unit_nb',
      unit: 'count',
      source: 'regulation',
    },
    {
      path: 'noise_sensibility_level',
      key: 'noise_sensitivity_level',
      source: 'regulation',
    },
    {
      path: 'building_area',
      key: 'building_area',
      unit: 'm2',
      source: 'regulation',
      transform: round1,
    },
    {
      path: 'construction_arrangement',
      key: 'construction_arrangement',
      source: 'regulation',
    },
  ],
}

const LAND_SPECS: SectionSpec[] = [
  {
    // Identity already carries the parcel's address, area and zone. What it
    // cannot carry are Popety's own indices, stored under `summary.scores` —
    // and `under_exploited_score` is the one figure a developer or a broker
    // pitching land value needs first. Absent from the export until 2026-09-07.
    id: 'summary',
    title: 'Parcel indices',
    definitions: POPETY_SCORE_DEFINITIONS,
    fields: [
      calc('scores.under_exploited_score', 'under_exploited_score', 'score'),
      calc('scores.development_score', 'development_score', 'score'),
      calc('scores.livability_score', 'livability_score', 'score'),
      calc('building_nb', 'building_count', 'count'),
      calc('average_building_year', 'average_building_year', 'year'),
    ],
  },
  {
    id: 'geo',
    title: 'Terrain and topography',
    provenance: {
      provider: 'swisstopo (swissALTI3D)',
      reference_url: 'https://www.swisstopo.admin.ch/',
    },
    fields: [
      // Slope-weighted circular mean of the parcel's aspect, not the modal
      // direction — the two disagree on terraced ground.
      {
        path: 'terrainMetrics.primaryAspect',
        key: 'dominant_orientation',
        source: 'spatial_analysis',
      },
      // Degrees, not percent: the same computation buckets these into
      // '0-5°' / '5-10°' bands, so a `pct` label would be a different quantity.
      calc('terrainMetrics.averageSlope', 'average_slope', 'deg'),
      calc('terrainMetrics.maxSlope', 'max_slope', 'deg'),
      // This one genuinely is 0-100.
      calc('terrainMetrics.flatAreaPercentage', 'flat_area_share', 'pct'),
      {
        path: 'terrainMetrics.maxFlatArea',
        key: 'largest_flat_area',
        unit: 'm2',
        source: 'spatial_analysis',
        transform: round1,
      },
      geo('altimetryData.altitude_min', 'altitude_min', 'm'),
      geo('altimetryData.altitude_max', 'altitude_max', 'm'),
      {
        path: 'terrainMetrics.reliableSlopeData',
        key: 'slope_data_is_reliable',
        source: 'spatial_analysis',
      },
    ],
  },
  {
    id: 'zoning',
    title: 'Zoning',
    provenance: { provider: 'Cantonal and communal land-use plans' },
    fields: [planRows],
  },
  {
    id: 'restrictions',
    title: 'Restrictions',
    provenance: { provider: 'Cantonal and communal land-use plans' },
    // Same stored shape as `zoning` but a different selection of plans:
    // identical in only 21 of 157 shares that carry both, so this is a
    // genuinely separate section rather than a duplicate.
    fields: [planRows],
  },
  {
    id: 'zoning_data',
    title: 'Applicable building indices',
    provenance: { provider: 'Cantonal and communal land-use plans' },
    fields: [
      ...indices('landData.'),
      reg('landData.mass_index', 'im_bmz', 'ratio'),
      {
        path: 'landData.max_height',
        key: 'max_height',
        unit: 'm',
        source: 'regulation',
        transform: round1,
      },
      reg('landData.max_floor_nb', 'max_floor_nb', 'count'),
      reg('landData.unit_nb', 'unit_nb', 'count'),
      // Below: buildable surfaces Popety derives from those indices and the
      // parcel area, so `spatial_analysis` rather than `regulation`.
      // Buildable-surface figures, public-API names: sb (surface bâtie),
      // sbd_agbf, sbpu, sp_gf, spd_agf, std_agsf, vbr_bvm (volume).
      calc('landData.sb', 'sb', 'm2'),
      calc('landData.sbd', 'sbd_agbf', 'm2'),
      calc('landData.sbpu', 'sbpu', 'm2'),
      calc('landData.sp', 'sp_gf', 'm2'),
      calc('landData.spd', 'spd_agf', 'm2'),
      calc('landData.std', 'std_agsf', 'm2'),
      calc('landData.vbr', 'vbr_bvm', 'm3'),
    ],
  },
  {
    id: 'buildings',
    title: 'Buildings on the parcel',
    provenance: {
      provider: 'Federal Register of Buildings and Dwellings (RegBL)',
    },
    fields: [
      calc('total_buildings', 'total_buildings', 'count'),
      {
        path: 'buildings',
        key: 'buildings',
        source: 'geoportal',
        kind: 'series',
        item: [
          {
            path: 'classification',
            key: 'popety_classification',
            source: 'geoportal',
          },
          {
            path: 'area',
            key: 'ground_floor_area_m2',
            unit: 'm2',
            source: 'geoportal',
            transform: round1,
          },
          {
            path: 'cover_area',
            key: 'cover_area_m2',
            unit: 'm2',
            source: 'geoportal',
            transform: round1,
          },
          // Stored as a 0-1 ratio; the public API publishes this key as a
          // percentage (`Math.round(coverAreaPerc * 100)`), so match it.
          {
            path: 'cover_area_perc',
            key: 'share_on_parcel_pct',
            unit: 'pct',
            source: 'geoportal',
            transform: (v: number) => Math.round(v * 100),
          },
          calc('floor_nb', 'floor_nb', 'count'),
          calc('underground_nb', 'underground_floor_nb', 'count'),
          calc('addresses_nb', 'addresses_nb', 'count'),
          {
            path: 'height',
            key: 'height_m',
            unit: 'm',
            source: 'geoportal',
            transform: round1,
          },
          {
            path: 'height_estimate',
            key: 'height_estimate_m',
            unit: 'm',
            source: 'spatial_analysis',
            transform: round1,
          },
          {
            path: 'volume_estimate',
            key: 'volume_estimate_m3',
            unit: 'm3',
            source: 'spatial_analysis',
            transform: round0,
          },
          {
            path: 'protected_rank',
            key: 'heritage_protection_rank',
            source: 'geoportal',
          },
        ],
      },
    ],
  },
  {
    id: 'access',
    title: 'EV charging access',
    provenance: { provider: 'swisstopo geo.admin.ch' },
    fields: [
      calc('searchRadius', 'search_radius', 'm'),
      {
        path: 'evStations',
        key: 'ev_charging_stations_nb',
        unit: 'count',
        source: 'geoportal',
        kind: 'length',
      },
    ],
  },
  {
    id: 'purchase_listings',
    title: 'Saved purchase listings',
    fields: [
      savedListingRows('', 'listings', 'CHF', 'CHF/m2'),
      savedListingPriceHistory('', 'asking_price_history', 'CHF'),
    ],
  },
  {
    id: 'rental_listings',
    title: 'Saved rental listings',
    fields: [
      savedListingRows('', 'listings', 'CHF/month', 'CHF/m2/month'),
      savedListingPriceHistory('', 'asking_price_history', 'CHF/month'),
    ],
  },
  {
    id: 'transactions',
    title: 'Saved transactions',
    provenance: { provider: 'Swiss land registry' },
    fields: [transactionRows('', 'transactions')],
  },
  {
    id: 'construction_permits',
    title: 'Saved construction permits',
    provenance: { provider: 'Cantonal building-permit publications' },
    fields: [savedPermitRows('', 'permits')],
  },
  {
    id: 'notes',
    title: 'Notes',
    fields: [
      {
        path: '',
        key: 'notes',
        source: 'user',
        kind: 'series',
        item: [
          { path: 'create_timestamp', key: 'written_on', source: 'user' },
          { path: 'author_name', key: 'author', source: 'user' },
          { path: 'content', key: 'content', source: 'user' },
          { path: 'type', key: 'scope', source: 'user' },
        ],
      },
    ],
  },
  {
    id: 'files',
    title: 'Files',
    fields: [
      {
        path: '',
        key: 'files',
        source: 'user',
        kind: 'series',
        item: [
          { path: 'name', key: 'name', source: 'user' },
          { path: 'type', key: 'type', source: 'user' },
          { path: 'size', key: 'size_bytes', source: 'user' },
          { path: 'create_timestamp', key: 'added_on', source: 'user' },
        ],
      },
    ],
  },
  marketSpec('purchase_market', 'purchase', 'Purchase market'),
  marketSpec('rental_market', 'rent', 'Rental market'),
  historySpec({ transactions: true, permits: true }),
  ...sharedAnalysisSpecs(),
]

export const LAND_SECTION_SPECS: SectionSpec[] = withDescriptions(
  LAND_SPECS,
  LAND_SECTION_DESCRIPTIONS
)
